import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { coursesApi } from '../../api/client'
import { PlusCircle, Edit2, Eye, EyeOff, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

export default function InstructorDashboardPage() {
  const qc = useQueryClient()

  // Fetch instructor's own courses — use /courses?instructor=me (a possible future filter)
  // For now, use the main catalog
  const { data, isLoading } = useQuery({
    queryKey: ['instructor-courses'],
    queryFn: () => coursesApi.list({ page_size: 100 } as any).then(r => r.data),
  })

  const publishMut = useMutation({
    mutationFn: ({ slug, published }: { slug: string; published: boolean }) =>
      coursesApi.update(slug, { is_published: published }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['instructor-courses'] }) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Update failed'),
  })

  const deleteMut = useMutation({
    mutationFn: (slug: string) => coursesApi.delete(slug),
    onSuccess: () => { toast.success('Course deleted'); qc.invalidateQueries({ queryKey: ['instructor-courses'] }) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Delete failed'),
  })

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold mb-1">Instructor Dashboard</h1>
          <p className="text-brand-muted">Manage your courses, lessons, and quizzes</p>
        </div>
        <Link to="/instructor/courses/new/edit" className="btn-primary">
          <PlusCircle size={16}/> New Course
        </Link>
      </div>

      {isLoading ? (
        <p className="text-brand-muted">Loading…</p>
      ) : (
        <div className="overflow-hidden border border-brand-border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-brand-surface2">
              <tr>
                <th className="text-left px-4 py-3 text-brand-muted font-semibold">Course</th>
                <th className="text-left px-4 py-3 text-brand-muted font-semibold">Level</th>
                <th className="text-center px-4 py-3 text-brand-muted font-semibold">Enrolled</th>
                <th className="text-center px-4 py-3 text-brand-muted font-semibold">Status</th>
                <th className="px-4 py-3"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {data?.items?.map((c: any) => (
                <tr key={c.id} className="hover:bg-brand-surface2 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-semibold">{c.title}</p>
                    <p className="text-xs text-brand-muted">{c.slug}</p>
                  </td>
                  <td className="px-4 py-3 capitalize text-brand-muted">{c.level}</td>
                  <td className="px-4 py-3 text-center">{c.enrolled_count}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`badge ${c.is_published ? 'badge-green' : 'badge-amber'}`}>
                      {c.is_published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/instructor/courses/${c.slug}/edit`} className="btn-ghost py-1 px-2 text-xs">
                        <Edit2 size={13}/> Edit
                      </Link>
                      <button className="btn-ghost py-1 px-2 text-xs" onClick={() => publishMut.mutate({ slug: c.slug, published: !c.is_published })}>
                        {c.is_published ? <EyeOff size={13}/> : <Eye size={13}/>}
                        {c.is_published ? 'Unpublish' : 'Publish'}
                      </button>
                      <button className="btn-ghost py-1 px-2 text-xs text-red-400" onClick={() => { if (confirm('Delete this course?')) deleteMut.mutate(c.slug) }}>
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data?.items?.length && (
            <div className="text-center py-16 text-brand-muted">
              <p>No courses yet.</p>
              <Link to="/instructor/courses/new/edit" className="btn-primary mt-4 inline-flex"><PlusCircle size={15}/> Create your first course</Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
